import { CalendarRange } from 'lucide-react';
import type { FilterState } from '../dashboardFilterUtils';
import { FilterSection, FilterField, filterControlClass } from '../../filters/FilterSection';

const QUICK_RANGES: Array<{ key: string; label: string }> = [
  { key: 'last12Months', label: 'Last 12 months' },
  { key: 'thisMonth', label: 'This month (live)' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'lastQuarter', label: 'Last quarter' },
  { key: 'thisYear', label: 'This year' },
  { key: 'lastYear', label: 'Last year' },
];

interface DateRangeSectionProps {
  filters: FilterState;
  setFilters: (filters: FilterState) => void;
  activeQuickRange: string | null;
  isLast12MonthsActive: boolean;
  setQuickDateRange: (range: string) => void;
  /** CR-4800: compliance data has no historical-vs-live split. */
  showDataMode: boolean;
}

/**
 * CR-6401: DATE RANGE group — quick-range presets, the explicit From/To dates,
 * and the Historical/Live data toggle that previously sat stranded at the
 * bottom of the panel next to the strategic-suppliers tickbox.
 */
export function DateRangeSection({
  filters,
  setFilters,
  activeQuickRange,
  isLast12MonthsActive,
  setQuickDateRange,
  showDataMode,
}: DateRangeSectionProps) {
  const isRangeActive = (key: string) =>
    key === 'last12Months' ? isLast12MonthsActive : activeQuickRange === key;

  return (
    <FilterSection title="Date Range" icon={CalendarRange}>
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex flex-wrap gap-2 max-w-2xl">
          {QUICK_RANGES.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setQuickDateRange(key)}
              className={`px-4 py-2 text-sm font-medium border rounded-md transition-colors ${
                isRangeActive(key)
                  ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                  : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap gap-4">
          <div className="w-44">
            <FilterField label="From date">
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
                className={filterControlClass}
              />
            </FilterField>
          </div>
          <div className="w-44">
            <FilterField label="To date">
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
                className={filterControlClass}
              />
            </FilterField>
          </div>
        </div>
      </div>

      {showDataMode && (
        <>
        <div className="flex items-center gap-3 mt-4">
          <span className="text-sm font-medium text-foreground">Data</span>
          <div className="flex">
            <button
              onClick={() => {
                if (activeQuickRange !== 'thisMonth') setFilters({ ...filters, dataMode: 'historical' });
              }}
              disabled={activeQuickRange === 'thisMonth'}
              className={`px-4 py-2 text-sm font-medium rounded-l-md border transition-colors ${
                activeQuickRange === 'thisMonth'
                  ? 'bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50'
                  : filters.dataMode === 'historical'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              Historical
            </button>
            <button
              onClick={() => setFilters({ ...filters, dataMode: 'live' })}
              className={`px-4 py-2 text-sm font-medium rounded-r-md border-t border-r border-b transition-colors ${
                filters.dataMode === 'live'
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              Live
            </button>
          </div>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Historical: past months use their frozen month-end arrears; a month with no snapshot shows no arrears. Live: every figure uses today&apos;s open overdue lines.
        </p>
        </>
      )}
    </FilterSection>
  );
}
